
import { defineComponent, ref, watch, computed, PropType, onMounted } from "vue"
import { Actions } from "@/store/enums/StoreEnums"
import { useStore } from "vuex"
import { ElMessage, ElMessageBox } from "element-plus"
import { QuestionFilled } from '@element-plus/icons-vue'
import { CardPoolItem } from "@/store/modules/CardPoolModule"
import { CardOption } from "@/store/modules/CardModule"
import { CardLanguageOption } from "@/store/modules/CardLanguageModule"
import { CardVersionOption } from "@/store/modules/CardVersionModule"

interface NewItemForm {
  card_ids: number[];
  weight: number;
}

export default defineComponent({
  components: {
    QuestionFilled
  },
  props: {
    cardPoolId: {
      type: Number as PropType<number>,
      required: true
    },
    cardSeriesId: {
      type: Number as PropType<number | null>,
      default: null
    },
    guaranteedRarities: {
      type: Array as PropType<string[]>,
      default: () => []
    }
  },
  emits: ['update'],
  setup(props, { emit }) {
    const store = useStore()
    const loading = ref(false)
    const saving = ref(false)
    const items = ref<CardPoolItem[]>([])
    const allCards = ref<CardOption[]>([])
    const addDialogVisible = ref(false)

    const newItem = ref<NewItemForm>({
      card_ids: [],
      weight: 100
    })

    const languageStoreOptions = ref<CardLanguageOption[]>([])
    const versionStoreOptions = ref<CardVersionOption[]>([])

    const cardFilter = ref({
      language_id: null as number | null,
      version_id: null as number | null,
      name: ''
    })

    // 加载语系、版本选项
    onMounted(() => {
      store.dispatch(Actions.GET_ALL_CARD_LANGUAGES).then(() => {
        languageStoreOptions.value = store.getters.currentAllCardLanguages
      })
      store.dispatch(Actions.GET_ALL_CARD_VERSIONS).then(() => {
        versionStoreOptions.value = store.getters.currentAllCardVersions
      })
    })

    // 级联：语系变化时过滤版本
    const filteredVersionOptions = computed(() => {
      if (!cardFilter.value.language_id) return versionStoreOptions.value
      return versionStoreOptions.value.filter(v => v.card_language_id === cardFilter.value.language_id)
    })

    watch(() => cardFilter.value.language_id, () => {
      // 如果当前选中的版本不属于新语系，清空版本选择
      if (cardFilter.value.version_id) {
        const valid = filteredVersionOptions.value.some(v => v.id === cardFilter.value.version_id)
        if (!valid) {
          cardFilter.value.version_id = null
        }
      }
    })

    // 计算可添加的卡牌（排除已添加的）
    const availableCards = computed(() => {
      const existingCardIds = items.value.map(item => item.card_id)
      return allCards.value.filter(card => !existingCardIds.includes(card.id))
    })

    // 根据筛选条件过滤可用卡牌
    const filteredAvailableCards = computed(() => {
      return availableCards.value.filter(card => {
        if (cardFilter.value.language_id && card.card_language_id !== cardFilter.value.language_id) return false
        if (cardFilter.value.version_id && card.card_version_id !== cardFilter.value.version_id) return false
        if (cardFilter.value.name && !card.name.includes(cardFilter.value.name)) return false
        return true
      })
    })

    const formatCardLabel = (card: CardOption) => {
      let label = card.name
      if (card.card_language_name) label += ` [${card.card_language_name}]`
      if (card.card_version_name) label += ` [${card.card_version_name}]`
      label += ` (${card.rarity_display || card.rarity})`
      return label
    }

    const loadItems = () => {
      if (!props.cardPoolId) return

      loading.value = true
      store
        .dispatch(Actions.GET_CARD_POOL_ITEMS, props.cardPoolId)
        .then(() => {
          items.value = store.getters.currentCardPoolItems
        })
        .catch(() => {
          ElMessage.error('获取卡池项目失败')
        })
        .finally(() => {
          loading.value = false
        })
    }

    const loadCards = () => {
      const params = props.cardSeriesId ? { card_series_id: props.cardSeriesId } : {}
      store.dispatch(Actions.GET_ALL_CARDS, params).then(() => {
        allCards.value = store.getters.currentAllCards
      })
    }

    watch(() => props.cardPoolId, () => {
      loadItems()
    }, { immediate: true })

    watch(() => props.cardSeriesId, () => {
      loadCards()
    }, { immediate: true })

    const getRarityTagType = (rarity?: string) => {
      const typeMap: Record<string, string> = {
        'R': 'info',
        'SR': '',
        'SSR': 'success',
        'SSSR': 'warning',
        'UR': 'danger',
        'SAR': 'danger'
      }
      return typeMap[rarity || ''] || ''
    }

    const isGuaranteedRarity = (row: CardPoolItem) => {
      const rarity = row.effective_level || row.card?.rarity
      return rarity ? props.guaranteedRarities.includes(rarity) : false
    }

    const onAddItem = () => {
      newItem.value = {
        card_ids: [],
        weight: 100
      }
      cardFilter.value = { language_id: null, version_id: null, name: '' }
      addDialogVisible.value = true
    }

    const selectAllCards = () => {
      const currentIds = new Set(newItem.value.card_ids)
      filteredAvailableCards.value.forEach(c => currentIds.add(c.id))
      newItem.value.card_ids = Array.from(currentIds)
    }

    const confirmAddItem = () => {
      if (newItem.value.card_ids.length === 0) {
        ElMessage.warning('请选择卡牌')
        return
      }

      saving.value = true
      const itemsPayload = newItem.value.card_ids.map(id => ({
        card_id: id,
        weight: newItem.value.weight
      }))

      store
        .dispatch(Actions.BATCH_CREATE_CARD_POOL_ITEMS, {
          card_pool_id: props.cardPoolId,
          items: itemsPayload
        })
        .then((data: { success: boolean; error_msg?: string; created_count?: number }) => {
          if (data.success) {
            ElMessage.success(`成功添加 ${data.created_count || itemsPayload.length} 张卡牌`)
            addDialogVisible.value = false
            loadItems()
            emit('update')
          } else {
            ElMessage.error(data.error_msg || '添加失败')
          }
        })
        .catch(() => {
          ElMessage.error('添加失败')
        })
        .finally(() => {
          saving.value = false
        })
    }

    const onItemChange = (row: CardPoolItem) => {
      store
        .dispatch(Actions.UPDATE_CARD_POOL_ITEM, {
          card_pool_id: props.cardPoolId,
          item_id: row.id,
          values: {
            weight: row.weight,
            guaranteed_weight: row.guaranteed_weight,
            sort: row.sort,
            is_animation: row.is_animation
          }
        })
        .then((data: { success: boolean; error_msg?: string }) => {
          if (data.success) {
            emit('update')
          } else {
            ElMessage.error(data.error_msg || '更新失败')
            loadItems()
          }
        })
        .catch(() => {
          ElMessage.error('更新失败')
          loadItems()
        })
    }

    const onDeleteItem = (row: CardPoolItem) => {
      ElMessageBox.confirm(
        `确定要从卡池移除卡牌「${row.card?.name}」吗？`,
        '删除确认',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
      )
        .then(() => {
          store
            .dispatch(Actions.DELETE_CARD_POOL_ITEM, {
              card_pool_id: props.cardPoolId,
              item_id: row.id
            })
            .then((data: { success: boolean; error_msg?: string }) => {
              if (data.success) {
                ElMessage.success('删除成功')
                loadItems()
                emit('update')
              } else {
                ElMessage.error(data.error_msg || '删除失败')
              }
            })
            .catch(() => {
              ElMessage.error('删除失败')
            })
        })
        .catch(() => {
          // 取消删除
        })
    }

    return {
      loading,
      saving,
      items,
      availableCards,
      filteredAvailableCards,
      languageStoreOptions,
      filteredVersionOptions,
      cardFilter,
      addDialogVisible,
      newItem,
      getRarityTagType,
      isGuaranteedRarity,
      formatCardLabel,
      onAddItem,
      selectAllCards,
      confirmAddItem,
      onItemChange,
      onDeleteItem
    }
  }
})
